// ABOUTME: Counts the saves that have been in flight long enough to be worth mentioning.
// ABOUTME: The link only counts; what the user sees is SlowSaveNotice, which subscribes here.

import { ApolloLink } from "@apollo/client"
import { Observable } from "@apollo/client/utilities"
import { useSyncExternalStore } from "react"
import { isMutationOperation } from "./operationKind"
import { mirrorSettling } from "./mirrorSettling"

/*
 * Reads have a bound (createTimeoutLink, 60 s); writes deliberately have none, because aborting one
 * is not the same as failing it — the server may well have applied it, and the client cannot tell.
 * So the answer to a save that hangs is not to cut it off but to stop pretending it is instant: the
 * request keeps running, and after a while the screen says so. If the answer arrives, everything
 * resolves the way it always did; nothing is lost by waiting, and nothing unknown is created.
 *
 * Module state with a subscription rather than context, like pendingDndOperation: the link lives
 * outside React entirely, and only the one component that draws the notice needs to re-render.
 */

/**
 * Long enough that no ordinary save reaches it, short enough to beat the user's own patience. The
 * mutations themselves run 84-297 ms (MULTI_ATHLETE_CALENDAR_PERF_SPEC §0) and the worst request of
 * any kind ever measured here is 27 749 ms (§0, HAR), so fifteen seconds sits well past every normal
 * save and well before the two-minute DnD watchdog that guards the calendar's own duplicate case.
 */
export const SLOW_MUTATION_NOTICE_MS = 15_000

let slowCount = 0
const listeners = new Set<() => void>()

const notify = (): void => {
  for (const listener of listeners) listener()
}

/** Store API, the shape changedRequirementPairs uses: a subscription and a getter. */
export const subscribeToSlowMutations = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** How many saves are currently past the threshold. One notice covers all of them. */
export const getSlowMutationCount = (): number => slowCount

export const useSlowMutationCount = (): number =>
  useSyncExternalStore(subscribeToSlowMutations, getSlowMutationCount, getSlowMutationCount)

/** Tests only: the count is module state and would otherwise leak between cases. */
export const resetSlowMutationsForTests = (): void => {
  slowCount = 0
  listeners.clear()
}

/**
 * Sits high in the chain — above retryLink and above the concurrency limiter — because the user is
 * waiting for all of it: the queue wait and every retry are part of how long the save is taking.
 */
export function createSlowMutationLink(noticeMs: number = SLOW_MUTATION_NOTICE_MS): ApolloLink {
  return new ApolloLink((operation, forward) => {
    if (!isMutationOperation(operation.query)) {
      return forward(operation)
    }

    return new Observable((observer) => {
      let timer: ReturnType<typeof setTimeout> | null = null
      let counted = false

      // Runs on completion, on failure and on teardown, so it has to be safe to call more than once.
      const settle = (): void => {
        if (timer !== null) {
          clearTimeout(timer)
          timer = null
        }
        if (!counted) return
        counted = false
        slowCount -= 1
        notify()
      }

      const subscription = mirrorSettling(forward(operation), observer, settle)

      timer = setTimeout(() => {
        timer = null
        counted = true
        slowCount += 1
        notify()
      }, noticeMs)

      return () => {
        settle()
        subscription.unsubscribe()
      }
    })
  })
}
